import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { prettyNumber } from '../../../utils/helpers';
import {
  AgencyHead,
  AgencyHeaderContainer,
  AgencyHeaderInfo,
  AgencyHeaderLogo,
  AgencyInfoViews,
  AgencyTitle,
  AgencyTitleSeoSuffix,
  InfoViewContainer
} from './styled';
import { ReadMore } from '../../Ad/ReadMore';

interface AgencyHeaderProps {
  title: string;
  h1?: string | null;
  views: number;
  apartmentCount: number;
  logoUrl?: string;
  description: string;
}

export const AgencyHeader: React.FC<AgencyHeaderProps> = memo(
  ({ title, h1, views = 0, apartmentCount = 0, logoUrl, description }) => {
    const { t } = useTranslation();

    const h1Parts = h1?.split(' — ');
    const h1Visible = h1Parts?.[0];
    const h1Suffix =
      h1Parts && h1Parts.length > 1
        ? ` — ${h1Parts.slice(1).join(' — ')}`
        : null;

    const prettyViews = useMemo(() => prettyNumber(views), [views]);
    const prettyApartmentCount = useMemo(
      () => prettyNumber(apartmentCount),
      [views]
    );

    return (
      <AgencyHeaderContainer>
        <AgencyHead>
          {logoUrl && (
            <AgencyHeaderLogo
              src={logoUrl}
              width={80}
              height={80}
              data-cy="agency-header__logo"
              alt={t('pages.agency.logo', {
                name: title
              })}
            />
          )}
          <AgencyHeaderInfo data-cy="agency-header__info">
            <AgencyTitle tag="h1" data-cy="agency-header__info-title">
              {h1Visible ?? t('pages.agency.title', { name: title })}
              {h1Suffix && (
                <AgencyTitleSeoSuffix>{h1Suffix}</AgencyTitleSeoSuffix>
              )}
            </AgencyTitle>
            <InfoViewContainer>
              <AgencyInfoViews tag="body_2" data-cy="agency-header__info-views">
                {t('pages.agency.views', { views_count: prettyViews })}
              </AgencyInfoViews>
              <AgencyInfoViews tag="body_2" data-cy="agency-header__info-views">
                {t('pages.agency.offers', {
                  apartment_count: prettyApartmentCount
                })}
              </AgencyInfoViews>
            </InfoViewContainer>
          </AgencyHeaderInfo>
        </AgencyHead>

        <ReadMore description={description} />
      </AgencyHeaderContainer>
    );
  }
);

AgencyHeader.displayName = 'AgencyHeader';
